import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { SortDirection } from "@angular/material/sort";

import { Observable } from "rxjs";

import { IncidentOverviewModel } from "../models/incident-overview.model";
import { IncidentHeaderModel } from "../models/incident-header.model";
import { PresetModel } from "../../preset/models/preset.model";
import { WorkspaceResult } from "@app/shared/models/Generic/workspace-result.model";
import { IncidentDetailsModel } from "../models/incident-details.model";
import { IncidentEditModel } from "../models/incident-edit.model";
import { IncidentCommentModel } from "../models/incident-comment.model";
import { IncidentCommentAddModel } from "../models/incident-comment-add.model";
import { IncidentMoiOverviewModel } from "../models/incident-moi-overview-model";
import { IncidentSearchModel } from "../models/incident-search.model";
import { HistoryChangeModel, HistoryMutationModel } from "../models/history-change-model";
import { IncidentWidgetDataModel } from "@app/event/shared/incident-widget-data-model";
import { BusinessNavigatorRequestModel } from "@app/navigators/business-navigator/models/business-navigator-request-model";
import { IncidentBusinessAssessment } from "../models/incident-business-assessment.model";

@Injectable()
export class IncidentService {
    private httpClient: HttpClient = inject(HttpClient);
    private baseUrl = `${localStorage.getItem("incidentUrl")}/api/incidents`;

    public hasAccessToIncident(incidentId: string): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseUrl}/hasaccess/${incidentId}`);
    }

    public getIncident(incidentId: string): Observable<IncidentDetailsModel> {
        return this.httpClient.get<IncidentDetailsModel>(`${this.baseUrl}/${incidentId}`);
    }

    public getEditIncidentRights(incidentId: string): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseUrl}/${incidentId}/editable`);
    }

    public getDeleteIncidentPermission(incidentId: string): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseUrl}/${incidentId}/deletable`);
    }

    public canReopenIncident(incidentId: string): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseUrl}/${incidentId}/reopen`);
    }

    public addIncident(incident: IncidentEditModel): Observable<string> {
        return this.httpClient.post<string>(`${this.baseUrl}`, incident);
    }

    public editIncident(
        incident: IncidentEditModel,
        files: { [id: string]: File },
    ): Observable<IncidentDetailsModel> {
        const formData = new FormData();

        formData.append("incident", JSON.stringify(incident));

        Object.entries(files).forEach(([key, value]) => formData.append(key, value));

        return this.httpClient.put<IncidentDetailsModel>(
            `${this.baseUrl}/${incident.id}`,
            formData,
        );
    }

    public deleteIncident(incidentId: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/${incidentId}`);
    }

    public getIncidentsForMoi(): Observable<IncidentMoiOverviewModel[]> {
        return this.httpClient.get<IncidentMoiOverviewModel[]>(`${this.baseUrl}/moi`);
    }

    public getIncidentSummaryForMoi(incidentId: string): Observable<IncidentMoiOverviewModel> {
        return this.httpClient.get<IncidentMoiOverviewModel>(
            `${this.baseUrl}/summary/${incidentId}`,
        );
    }

    public getIncidentsByGuids(incidentGuids: string[]): Observable<any[]> {
        return this.httpClient.post<any[]>(`${this.baseUrl}/incidentsbyguids`, incidentGuids);
    }

    public getIncidents(
        offset: number,
        count: number,
        sortBy?: string,
        sortdirection?: SortDirection,
        searchValue?: string,
        presetId?: string,
        preset?: PresetModel,
    ): Observable<WorkspaceResult<IncidentOverviewModel>> {
        const searchModel: IncidentSearchModel = {
            offset: offset,
            count: count,
            sortBy: sortBy,
            sortdirection: sortdirection === "asc",
            searchValue: searchValue,
            presetId: presetId,
            preset: JSON.stringify(preset),
        };

        return this.httpClient.post<WorkspaceResult<IncidentOverviewModel>>(
            `${this.baseUrl}/search`,
            searchModel,
        );
    }

    public exportIncidents(
        dateFormat: string,
        timeFormat: string,
        timeZone: string,
        sortBy?: string,
        sortdirection?: SortDirection,
        searchValue?: string,
        presetId?: string,
        preset?: PresetModel,
    ): Observable<Blob> {
        const exportSearchModel: IncidentSearchModel = {
            dateFormat: dateFormat,
            timeFormat: timeFormat,
            timeZone: timeZone,
            sortBy: sortBy,
            sortdirection: sortdirection === "asc",
            searchValue: searchValue,
            presetId: presetId,
            preset: JSON.stringify(preset),
        };

        return this.httpClient.post<Blob>(`${this.baseUrl}/export`, exportSearchModel, {
            responseType: "blob" as "json",
        });
    }

    public exportIncidentsDetails(dateFormat: string, timeFormat: string, timeZone: string) {
        return this.httpClient.get<Blob>(`${this.baseUrl}/incidentsdetailsexport`, {
            responseType: "blob" as "json",
            params: {
                dateFormat: dateFormat,
                timeFormat: timeFormat,
                timeZone: timeZone,
            },
        });
    }

    public downloadDocument(
        incidentId: string,
        documentId: string,
        documentBlobId: string,
    ): Observable<Blob> {
        return this.httpClient.get<Blob>(
            `${this.baseUrl}/${incidentId}/documents/${documentId}/${documentBlobId}/download`,
            {
                responseType: "blob" as "json",
            },
        );
    }

    public getComments(incidentId: string): Observable<IncidentCommentModel[]> {
        return this.httpClient.get<IncidentCommentModel[]>(
            `${this.baseUrl}/${incidentId}/comments`,
        );
    }

    public addComment(
        incidentId: string,
        comment: IncidentCommentAddModel,
    ): Observable<IncidentCommentModel> {
        return this.httpClient.put<IncidentCommentModel>(
            `${this.baseUrl}/${incidentId}/comments`,
            comment,
        );
    }

    public getHeaders(presetId?: string): Observable<IncidentHeaderModel[]> {
        let params = new HttpParams();
        if (presetId) {
            params = params.append("presetId", presetId);
        }

        return this.httpClient.get<IncidentHeaderModel[]>(`${this.baseUrl}/columns`, {
            params: params,
        });
    }

    public reopenIncident(incidentId: string, comment: string): Observable<IncidentDetailsModel> {
        return this.httpClient.put<IncidentDetailsModel>(`${this.baseUrl}/${incidentId}/reopen`, {
            comment: comment,
        });
    }

    public getWidgetInfo(filters: any) {
        const url = `${this.baseUrl}/widgetinfo`;
        return this.httpClient.post<IncidentWidgetDataModel>(url, filters);
    }

    public getWorkspaceDataFromWidget(
        filters: any,
    ): Observable<WorkspaceResult<IncidentOverviewModel>> {
        const url = `${this.baseUrl}/workspacewidgetdata`;
        return this.httpClient.post<WorkspaceResult<IncidentOverviewModel>>(url, filters);
    }

    public getIncidentMutations(incidentId: string): Observable<HistoryMutationModel[]> {
        return this.httpClient.get<HistoryMutationModel[]>(`${this.baseUrl}/${incidentId}/history`);
    }

    public getIncidentChanges(
        incidentId: string,
        changeDate: Date,
    ): Observable<HistoryChangeModel[]> {
        var timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let params = new HttpParams();
        params = params.append("timeZone", timeZone);

        return this.httpClient.get<HistoryChangeModel[]>(
            `${this.baseUrl}/${incidentId}/history/${changeDate}`,
            {
                params: params,
            },
        );
    }

    public getCount(presetId: string): Observable<number> {
        if (!presetId) {
            presetId = "00000000-0000-0000-0000-000000000000";
        }
        return this.httpClient.get<number>(`${this.baseUrl}/count/${presetId}`);
    }

    public getIncidentsForBusinessAssessment(requestModel: BusinessNavigatorRequestModel) {
        const orgId = requestModel.organizationID ?? "";
        const bdId = requestModel.businessID ?? "";

        let params = new HttpParams();
        params = params.append("orgId", orgId);
        params = params.append("bdId", bdId);

        return this.httpClient.get<IncidentBusinessAssessment[]>(
            `${this.baseUrl}/businessassessment`,
            {
                params: params,
            },
        );
    }
}
